import { useQuery } from '@tanstack/react-query'
import { comparisonService } from '@/services/comparisonService'
import type {
  ComparisonSubject,
  ComparisonApiResponse,
  ProgressiveComparisonState,
  SubjectWithAggregations,
} from '@/types/comparison'

/**
 * Build a stable cache key for a single subject.
 */
function buildSingleCacheKey(subject: ComparisonSubject): string {
  const id = Array.isArray(subject.id)
    ? subject.id.sort().join(',')
    : String(subject.id)
  return `${subject.type}-${id}`
}

/**
 * Build a stable cache key for the combined comparison.
 */
function buildCombinedCacheKey(
  subjectA: ComparisonSubject,
  subjectB: ComparisonSubject
): string {
  return `${buildSingleCacheKey(subjectA)}__${buildSingleCacheKey(subjectB)}`
}

/**
 * Extract subject data from a single-subject API response.
 *
 * The backend uses the same ComparisonApiResponse format,
 * so we look for data.subjectA or data.subjectB depending on which was sent.
 */
function extractSubject(
  data: ComparisonApiResponse | undefined,
  side: 'A' | 'B'
): SubjectWithAggregations | null {
  if (!data?.data) return null
  const d = data.data as any
  // Primary: standard field from the compare endpoint
  if (side === 'A' && d.subjectA) return d.subjectA
  if (side === 'B' && d.subjectB) return d.subjectB
  // Fallback: if backend returns a single "subject" field
  if (d.subject) return d.subject
  return null
}

/**
 * Hook for progressive comparison loading.
 *
 * Fires 3 independent queries:
 * 1. Single subject A - fires as soon as subjectA is provided
 * 2. Single subject B - fires as soon as subjectB is provided
 * 3. Combined comparison - fires when both A and B have resolved successfully
 *
 * This allows the UI to show data progressively:
 * - Show column A data as soon as it arrives (with skeleton for B)
 * - Show column B data as soon as it arrives
 * - Update both columns with deltas/winners when combined resolves
 *
 * @param subjectA - Subject A (null if not yet selected)
 * @param subjectB - Subject B (null if not yet selected)
 * @returns Progressive comparison state
 */
export function useProgressiveCompare(
  subjectA: ComparisonSubject | null,
  subjectB: ComparisonSubject | null,
  includeOwnChannels: boolean = false
): ProgressiveComparisonState {
  const staleTime = 5 * 60 * 1000
  const iocParam = includeOwnChannels ? 'true' : 'false'

  // Query A: fetch single subject A
  // Cache key includes side ('A') to avoid collisions when A and B are the same subject
  const queryA = useQuery({
    queryKey: ['comparison', 'single', 'A', subjectA ? buildSingleCacheKey(subjectA) : 'none', iocParam],
    queryFn: async () => {
      if (!subjectA) throw new Error('Subject A is required')
      return await comparisonService.compareSingle({ subjectA, include_own_channels: iocParam })
    },
    enabled: !!subjectA,
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })

  // Query B: fetch single subject B
  const queryB = useQuery({
    queryKey: ['comparison', 'single', 'B', subjectB ? buildSingleCacheKey(subjectB) : 'none', iocParam],
    queryFn: async () => {
      if (!subjectB) throw new Error('Subject B is required')
      return await comparisonService.compareSingle({ subjectB, include_own_channels: iocParam })
    },
    enabled: !!subjectB,
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })

  // Query Combined: fetch full comparison when both singles are done
  const queryCombined = useQuery({
    queryKey: [
      'comparison',
      'combined',
      subjectA && subjectB
        ? buildCombinedCacheKey(subjectA, subjectB)
        : 'none-combined',
      iocParam,
    ],
    queryFn: async () => {
      if (!subjectA || !subjectB) {
        throw new Error('Both subjects are required for combined comparison')
      }
      return await comparisonService.compare({ subjectA, subjectB, include_own_channels: iocParam })
    },
    enabled: !!subjectA && !!subjectB && queryA.isSuccess && queryB.isSuccess,
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })

  const isLoadingA = queryA.isLoading && !!subjectA
  const isLoadingB = queryB.isLoading && !!subjectB
  const isLoadingCombined = queryCombined.isLoading && queryA.isSuccess && queryB.isSuccess

  return {
    subjectA: extractSubject(queryA.data, 'A'),
    isLoadingA,
    errorA: queryA.error as Error | null,
    subjectB: extractSubject(queryB.data, 'B'),
    isLoadingB,
    errorB: queryB.error as Error | null,
    combined: queryCombined.data?.data ?? null,
    isLoadingCombined,
    errorCombined: queryCombined.error as Error | null,
    isAnyLoading: isLoadingA || isLoadingB || isLoadingCombined,
    isComplete: queryCombined.isSuccess,
  }
}
